import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { exhaustMap, map, catchError, switchMap } from 'rxjs/operators';
import { of, from } from 'rxjs';
import {
    CreateProfileUIActions,
    CreateUserAccountBtnClicked,
} from './actions/create-profile-ui-actions';
import {
    AccountCreatedAction,
    AccountCreationFailedAction,
    AccountCreationFailedActionProps,
} from './actions/create-profile-service-actions';
import {
    RegistrationService,
    Country,
    CreateUserAccount201Response,
    AccountPreferenceValidator,
    NotificationPreferenceValidator,
    PostUserAccountRequest,
    PostUserAccountRequestUser,
    PostUserAccountRequestOrganization,
} from '@viewbid/ts-angular-client';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { AppState } from '../../../state/app-state';
import { selectUser } from '../../../state/app-selectors';
import { User, JWTTokens, TokenStorageService } from 'viewbid-shared-lib';
import { getViewbidAPIErrorMessageFromBadRequestResponse } from '../../../utils/error-parser';
import { NotificationPreferences } from '../model/notification-preference';
import { trimStreetLine } from '../validators/street-line-validator';

@Injectable()
export class CreateProfileEffects {
    public user$: Observable<User | null>;
    public user: User | null = null;
    createUserProfileListener$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(CreateProfileUIActions.CreateUserAccountBtnClicked),
            exhaustMap((action: CreateUserAccountBtnClicked) => {
                const notificationPreferences: AccountPreferenceValidator =
                    getAccountPreferences(action.payload.preferences);

                const user: PostUserAccountRequestUser = {
                    firstname: action.payload.firstname,
                    lastname: action.payload.lastname,
                    username: action.payload.userName,
                    address: {
                        city: action.payload.city,
                        country: action.payload.country as Country,
                        address1: trimStreetLine(action.payload.address1),
                        address2: trimStreetLine(action.payload.address2),
                        province: action.payload.province,
                        buzzer: action.payload.buzzer,
                        postalCode: action.payload.postalCode,
                    },
                    email: this.user?.email ?? '',
                    ssoId: this.user?.ssoId ?? '',
                    telephone: {
                        phoneNumber: action.payload.phoneNumber,
                        phoneNumberExt: action.payload.phoneNumberExt,
                    },
                    notificationPreferences,
                };

                let organization:
                    | PostUserAccountRequestOrganization
                    | undefined = undefined;

                if (action.payload.organization) {
                    organization = {
                        type: action.payload.organization.type!,
                        name: action.payload.organization.name,
                        representativeFullName:
                            action.payload.organization.representativeFullName,
                        number: action.payload.organization.businessNumber,
                        taxType: action.payload.organization.taxType!,
                        organizationType: 'COMPANY',
                        address: {
                            city: action.payload.organization.city,
                            country: action.payload.organization
                                .country as Country,
                            address1: trimStreetLine(
                                action.payload.organization.address1,
                            ),
                            address2: trimStreetLine(
                                action.payload.organization.address2,
                            ),
                            province: action.payload.organization.province,
                            buzzer: action.payload.organization.buzzer,
                            postalCode: action.payload.organization.postalCode,
                        },
                        telephone: {
                            phoneNumber:
                                action.payload.organization.phoneNumber,
                            phoneNumberExt:
                                action.payload.organization.phoneNumberExt,
                        },
                    };
                }

                const request: PostUserAccountRequest = { user, organization };

                return this.registrationService.createUserAccount(request).pipe(
                    switchMap((result: CreateUserAccount201Response) =>
                        from(this.tokenStorageService.getJWTToken()).pipe(
                            map((tokens: JWTTokens) => {
                                this.tokenStorageService.storeJWTToken(tokens);
                                return new AccountCreatedAction(result);
                            }),
                        ),
                    ),
                    catchError((error: Error) => {
                        const errorMessage: string | null =
                            getViewbidAPIErrorMessageFromBadRequestResponse(
                                error,
                            );
                        if (errorMessage) {
                            return of(
                                new AccountCreationFailedAction(
                                    new AccountCreationFailedActionProps(
                                        errorMessage,
                                    ),
                                ),
                            );
                        }
                        return of(
                            new AccountCreationFailedAction(
                                new AccountCreationFailedActionProps(
                                    error.message,
                                ),
                            ),
                        );
                    }),
                );
            }),
        );
    });

    constructor(
        private actions$: Actions,
        private registrationService: RegistrationService,
        private store: Store<AppState>,
        private tokenStorageService: TokenStorageService,
    ) {
        this.user$ = this.store.select<User | null>(selectUser);
        this.user$.subscribe((user: User | null) => {
            this.user = user;
        });
    }
}

const CHANNELS_BY_PREFERENCE: Record<
    NotificationPreferences,
    NotificationPreferenceValidator
> = {
    [NotificationPreferences.All]: { SMS: true, email: true },
    [NotificationPreferences.TEXT_ONLY]: { SMS: true, email: false },
    [NotificationPreferences.EMAIL_ONLY]: { SMS: false, email: true },
};

export function getAccountPreferences(
    preference: string,
): AccountPreferenceValidator {
    const channels: NotificationPreferenceValidator = CHANNELS_BY_PREFERENCE[
        preference as NotificationPreferences
    ] ?? { SMS: false, email: false };
    return { notificationPreferences: [channels] };
}
